import { useCallback } from "react";

import { requestMagicLinkForSignIn } from "./use-handle-magic-link-sign-in";
import {
  AuthMessages,
  AuthMessageTypesEnum,
  AuthPagesEnum,
  MAGIC_LINK_SIGN_IN_METHOD,
  SignInStatusEnum,
  SignUpNextStepEnum,
  SignUpStatusEnum,
} from "../constants";
import { CognitoAuthInput, signUpRetry } from "../utils/auth-methods";
import { handleCognitoError, MamaCognitoError } from "../utils/cognito-errors";
import { useClientMetaData } from "~/auth/hooks/use-client-meta-data";
import {
  SpecialNavigationRoutes,
  useMamaNavigate,
} from "~/navigation/mama-navigate";

export const useHandleMagicLinkSignUp = (): (({
  name,
  email,
}: {
  name: string;
  email: string;
}) => Promise<boolean>) => {
  const navigate = useMamaNavigate();

  const clientMetadata = useClientMetaData({
    signInMethod: MAGIC_LINK_SIGN_IN_METHOD,
    isSignup: "yes",
    alreadyHaveMagicLink: "no",
  });

  return useCallback(
    async ({
      name,
      email,
    }: {
      name: string;
      email: string;
    }): Promise<boolean> => {
      try {
        const signUpState = await signUpThenRequestMagicLink({
          email,
          name,
          clientMetadata,
        });
        switch (signUpState) {
          case SignUpStatusEnum.MAGIC_LINK_SENT:
            navigate(AuthPagesEnum.AUTH_ROOT, {
              info: AuthMessages[AuthMessageTypesEnum.CONFIRM_SIGN_UP],
              replace: true,
            });
            return true;
          case SignUpStatusEnum.USER_ALREADY_EXISTS:
            navigate(AuthPagesEnum.MAGIC_SIGN_IN, {
              replace: true,
              error: {
                tx: "auth.signIn.errors.AnAccountWithThisEmailAlreadyExists",
              },
            });
            return false;
          default:
            throw new MamaCognitoError({
              name: "auth.signIn.errors.UnknownErorHasOccured",
            });
        }
      } catch (error) {
        navigate(SpecialNavigationRoutes.AUTH_FALLBACK, {
          replace: true,
          error: handleCognitoError(error, { email }),
        });
        return false;
      }
    },
    [clientMetadata, navigate],
  );
};

export const signUpThenRequestMagicLink = async ({
  email,
  name,
  clientMetadata,
}: CognitoAuthInput): Promise<SignUpStatusEnum> => {
  try {
    const signInResult = await requestMagicLinkForSignIn({
      email,
      name,
      clientMetadata,
    });

    if (signInResult === SignInStatusEnum.MAGIC_LINK_SENT) {
      return SignUpStatusEnum.USER_ALREADY_EXISTS;
    }
  } catch (error) {
    return SignUpStatusEnum.UNKNOWN_SIGN_IN_BEFORE_SIGN_UP_ERROR;
  }

  try {
    const { isSignUpComplete, nextStep, userId } = await signUpRetry({
      username: email,
      password: generatePassword(),
      options: {
        userAttributes: {
          name,
          email,
        },
        autoSignIn: false,
        clientMetadata,
      },
    });

    if (!isSignUpComplete || nextStep.signUpStep !== SignUpNextStepEnum.DONE) {
      return SignUpStatusEnum.SIGN_UP_NOT_DONE;
    }

    if (!userId) {
      return SignUpStatusEnum.SIGN_UP_NOT_DONE;
    }

    const signInStatus = await requestMagicLinkForSignIn({
      email,
      name,
      clientMetadata,
    });

    if (signInStatus === SignInStatusEnum.MAGIC_LINK_SENT) {
      return SignUpStatusEnum.MAGIC_LINK_SENT;
    }

    return SignUpStatusEnum.UNKNOWN_ERROR;
  } catch {
    return SignUpStatusEnum.UNKNOWN_ERROR;
  }
};

const DEFAULT_LENGTH = 16;
const CHARS = [
  "abcdefghijklmnopqrstuvwwyz",
  "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  "0123456890",
  "+-^$*.[]{}()?\"!@#%&/,><':;|_~",
];

const generatePassword = (): string => {
  let indexHistory: number[] = [];
  const gen = () => {
    let p = "";
    for (let i = 0; i < DEFAULT_LENGTH; i++) {
      const idx = Math.floor(Math.random() * 4);
      indexHistory.push(idx);

      const set = CHARS[idx];
      const l = set.length;
      const idx2 = Math.floor(Math.random() * l);
      const chr = set[idx2];
      p = p += chr;
    }
    return p;
  };

  const password = gen();
  if (
    indexHistory.includes(0) &&
    indexHistory.includes(1) &&
    indexHistory.includes(2) &&
    indexHistory.includes(3)
  ) {
    return password;
  } else {
    indexHistory = [];
    return gen();
  }
};
